import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  InputAdornment,
  IconButton,
  Alert,
  CircularProgress,
  Paper,
  Tooltip,
  Grid
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  CheckCircle as CheckIcon,
  Link as LinkIcon,
  Share as ShareIcon,
  Send as SendIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useLanguage } from '../../Commun/contexts/LanguageContext';
import { blobticService } from '../../Commun/services/blobticService';

interface PublicPreRegistrationModalProps {
  open: boolean;
  onClose: () => void;
  dosId?: number;
}

export const PublicPreRegistrationModal: React.FC<PublicPreRegistrationModalProps> = ({
  open,
  onClose,
  dosId
}) => {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [generatedUrl, setGeneratedUrl] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  const handleGenerateLink = async (forceNew: boolean = false, overrideStartDate?: string, overrideEndDate?: string) => {
    setLoading(true);
    setError(null);
    try {
      const sDateVal = overrideStartDate !== undefined ? overrideStartDate : startDate;
      const eDateVal = overrideEndDate !== undefined ? overrideEndDate : endDate;

      const res = await blobticService.executeZoomRequest<any>({
        ZoomKey: 'Blobtic_API',
        Action: {
          OKey: 'Blobtic_API,sys_api,generate_public_link',
          DOS_IDF: dosId,
          API_PAGE: 'student-pre-registration',
          FORCE_NEW: forceNew,
          API_START_DATE: sDateVal || null,
          API_END_DATE: eDateVal || null
        }
      });

      if (res && res.success && res.data) {
        const item = Array.isArray(res.data) ? res.data[0] : res.data;
        const token = item?.API_TOKEN || item?.token || '';
        const origin = window.location.origin;
        const fullUrl = `${origin}/student-pre-registration?token=${token}`;
        setGeneratedUrl(fullUrl);

        if (item?.API_START_DATE) {
          const s = item.API_START_DATE.split('T')[0];
          setStartDate(s);
        }
        if (item?.API_END_DATE) {
          const e = item.API_END_DATE.split('T')[0];
          setEndDate(e);
        }
      } else {
        setError(res?.message || t('academy.prereg.modal.error_generate'));
      }
    } catch (err: any) {
      setError(err?.message || t('academy.prereg.modal.error_generic'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      handleGenerateLink(false);
    } else {
      setGeneratedUrl('');
      setError(null);
    }
  }, [open]);

  const handleCopy = () => {
    if (!generatedUrl) return;
    navigator.clipboard.writeText(generatedUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 3000);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, borderBottom: 1, borderColor: 'divider' }}>
        <LinkIcon color="primary" />
        <Typography variant="h6" fontWeight={700}>
          {t('academy.prereg.modal.title')}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, pb: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t('academy.prereg.modal.subtitle')}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
            {error}
          </Alert>
        )}

        {loading && !generatedUrl ? (
          <Box sx={{ py: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <CircularProgress size={40} color="primary" />
            <Typography variant="body2" color="text.secondary">
              {t('academy.prereg.modal.btn_generating')}
            </Typography>
          </Box>
        ) : generatedUrl ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              {t('academy.prereg.modal.is_existing_notice')}
            </Alert>

            <TextField
              size="small"
              fullWidth
              value={generatedUrl}
              inputProps={{ readOnly: true }}
              variant="outlined"
              label={t('academy.prereg.modal.url_label')}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title={copied ? t('academy.prereg.modal.tooltip_copied') : t('academy.prereg.modal.tooltip_copy')}>
                      <IconButton color={copied ? 'success' : 'primary'} onClick={handleCopy}>
                        {copied ? <CheckIcon /> : <CopyIcon />}
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                )
              }}
            />

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Date de début (Optionnel)"
                  type="date"
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={startDate}
                  onChange={(e) => {
                    setStartDate(e.target.value);
                    if (generatedUrl) handleGenerateLink(false, e.target.value, endDate);
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Date d'expiration (Optionnel)"
                  type="date"
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={endDate}
                  onChange={(e) => {
                    setEndDate(e.target.value);
                    if (generatedUrl) handleGenerateLink(false, startDate, e.target.value);
                  }}
                />
              </Grid>
            </Grid>

            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              <Button
                variant="outlined"
                color="primary"
                fullWidth
                startIcon={copied ? <CheckIcon /> : <CopyIcon />}
                onClick={handleCopy}
                sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 600 }}
              >
                {copied ? t('academy.prereg.modal.btn_copied') : t('academy.prereg.modal.btn_copy')}
              </Button>
              <Button
                variant="contained"
                color="success"
                fullWidth
                component="a"
                href={`https://api.whatsapp.com/send?text=${encodeURIComponent(t('academy.prereg.modal.whatsapp_text') + generatedUrl)}`}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 600 }}
              >
                {t('academy.prereg.modal.btn_whatsapp')}
              </Button>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
              <Button
                size="small"
                color="inherit"
                disabled={loading}
                startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <RefreshIcon fontSize="small" />}
                onClick={() => handleGenerateLink(true)}
                sx={{ textTransform: 'none', fontSize: '0.8rem', opacity: 0.75 }}
              >
                {t('academy.prereg.modal.btn_regenerate')}
              </Button>
            </Box>
          </Box>
        ) : (
          <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 3, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : '#f8fafc' }}>
            <ShareIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1.5, opacity: 0.8 }} />
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
              {t('academy.prereg.modal.custom_link_title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 400, mx: 'auto' }}>
              {t('academy.prereg.modal.custom_link_desc')}
            </Typography>
            <Grid container spacing={2} sx={{ maxWidth: 450, mx: 'auto', mb: 3 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Date de début (Optionnel)"
                  type="date"
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Date d'expiration (Optionnel)"
                  type="date"
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </Grid>
            </Grid>
            <Button
              variant="contained"
              color="primary"
              size="large"
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
              onClick={() => handleGenerateLink(false)}
              disabled={loading}
              sx={{ borderRadius: 2, textTransform: 'none', px: 4, fontWeight: 700 }}
            >
              {loading ? t('academy.prereg.modal.btn_generating') : t('academy.prereg.modal.btn_generate')}
            </Button>
          </Paper>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2.5, borderTop: 1, borderColor: 'divider' }}>
        <Button onClick={onClose} color="inherit" sx={{ textTransform: 'none', fontWeight: 600 }}>
          {t('btn.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
